import { FC, useCallback, useEffect, useMemo } from "react";
import DataTable, { CellSize } from "../table";
import { GridActionsCellItem, GridAddIcon, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { assetService, cacheService, hlsChunkerService, mediaService } from "../../../Core/Api/services";
import { useDispatch } from "react-redux";
import {
    AddToSequence,
    GetLinkedToPreviewActionId,
    GetLoadedAssets,
    GetSelectedAsset,
    SetInAndOutFrame,
    SetLinkedToPreviewActionId,
    SetPreviewSrc,
    SetSelectedAsset,
    SetSelectedAssetFile,
    SetShowSequence,
    SetPreviewIsPlaying,
    SetPreviewTime,
    GetFetchingVideoFileData,
    SetFetchingVideoFileData,
} from "../../../Redux/Reducers/redux-slice-timetool";
import { useSelector } from "react-redux";
import AssetClassChip from "../../Misc/Chips/AssetChip/asset-class-chip";
import {
    GridApi,
    getDataGridUtilityClass,
    DataGridProProps,
    GridEventListener,
    GridGroupingColDefOverride,
    GridRenderCellParams,
    GridGroupNode,
    useGridApiContext,
    useGridRootProps,
    GridRowModelUpdate,
} from "@mui/x-data-grid-pro";
import { unstable_composeClasses as composeClasses, styled } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { SecToTimecode } from "../../../Utils/Timecode/ReactPlayer";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { routePaths } from "../../../Router/router";
import { useTranslation } from "react-i18next";

interface TimetoolMediaLibraryTableProps {
    filterText: string;
}

const TimetoolMediaLibraryTable: FC<TimetoolMediaLibraryTableProps> = ({ filterText }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const rows = useSelector(GetLoadedAssets);
    const customSnackbar = useCustomSnackbar();
    type Row = (typeof rows)[number];
    const fetchingVideoFileData = useSelector(GetFetchingVideoFileData);
    const selectedAsset = useSelector(GetSelectedAsset);
    const linkedAction = useSelector(GetLinkedToPreviewActionId);

    const handleSelectAsset = useCallback(
        (id: number, frameIn?: number, frameOut?: number) => {
            mediaService.GetFilesByObjectId(id).then((res) => {
                const selectedFile = res.files?.find((x) => x.category === "Proxy");
                if (!selectedFile) return;
                dispatch(SetShowSequence(false));
                dispatch(SetSelectedAssetFile(selectedFile));
                dispatch(SetSelectedAsset(rows.find((x) => x.id === id)));
                dispatch(
                    SetPreviewSrc(
                        selectedFile?.id
                            ? `${hlsChunkerService.GetServiceUrl()}/${selectedFile.id}/${cacheService.appData?.platformId}`
                            : ""
                    )
                );
                dispatch(SetPreviewIsPlaying(false));
                dispatch(SetPreviewTime(0));
                frameIn &&
                    frameOut &&
                    setTimeout(() => {
                        dispatch(SetInAndOutFrame({ inFrame: frameIn!, outFrame: frameOut! }));
                    }, 1000);

                dispatch(SetLinkedToPreviewActionId(""));
            });
        },
        [rows]
    );

    const handleAddToSequence = useCallback((asset: any) => {
        dispatch(SetFetchingVideoFileData(true));
        mediaService
            .GetStreamingSmilByObjectId(asset.assetId, null)
            .then(function (data) {
                dispatch(SetFetchingVideoFileData(false));

                const rowSrc = data.smilFullUrl;
                if (rowSrc) {
                    dispatch(AddToSequence({ asset, rowSrc }));
                } else {
                    customSnackbar(data.message ?? t("alert:error.requestingVideoData"), "error");
                }
            })
            .catch((err) => {
                dispatch(SetFetchingVideoFileData(false));
                customSnackbar(t("alert:error.lodingVideoFileData"), "error");
            });
    }, []);

    const getSelectedRows = useMemo(() => {
        if (!selectedAsset?.id) return [];
        return rows.findIndex((x) => x.id === selectedAsset.id) >= 0
            ? [rows.find((x) => x.id === selectedAsset.id).id]
            : [];
    }, [selectedAsset?.id, linkedAction, rows]);

    const cols: GridColDef[] = useMemo(
        () => [
            {
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                field: "classification",
                headerName: t("common:class"),
                renderCell: (data: Row) => <AssetClassChip label={data.value} classification={data.value} />,
            },
            {
                flex: CellSize.large,
                minWidth: CellSize.large,
                field: "name",
                headerName: t("common:name"),
                renderCell: (data: Row) => (
                    <Typography whiteSpace="normal" textOverflow="clip" variant="body2">
                        {data.value}
                    </Typography>
                ),
            },
            {
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                field: "duration",
                headerName: t("common:duration"),
                valueGetter: (data: Row, row: any) => {
                    if (!row) return;
                    return `${SecToTimecode(Math.round(row?.duration), row?.fps ?? 25)}`;
                },
                renderCell: (data: Row) => <Typography>{data.value}</Typography>,
            },
            {
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                field: "actions",
                type: "actions",
                align: "right",
                getActions: (params: GridRowParams) => [
                    <Tooltip title={t("common:preview")}>
                        <GridActionsCellItem
                            icon={
                                <VisibilityIcon
                                    sx={{ width: 20, height: 20, color: fetchingVideoFileData ? "gray" : "text.primary" }}
                                />
                            }
                            label={t("common:preview")}
                            disabled={fetchingVideoFileData}
                            onClick={(e) => {
                                handleSelectAsset(params?.row?.assetId, params?.row?.frameIn, params?.row?.frameOut);
                            }}
                        />
                    </Tooltip>,
                    <Tooltip title={t("common:addToSequence")}>
                        <GridActionsCellItem
                            disabled={fetchingVideoFileData}
                            icon={
                                <GridAddIcon sx={{ width: 20, height: 20, color: fetchingVideoFileData ? "gray" : "text.primary" }} />
                            }
                            label={t("common:addToSequence")}
                            onClick={(e) => handleAddToSequence(params.row)}
                        />
                    </Tooltip>,
                ],
            },
        ],
        [rows, fetchingVideoFileData]
    );

    const apiRef = useGridApiRef();

    const updateRows = useCallback((apiRef: React.MutableRefObject<GridApi>, rows: GridRowModelUpdate[]) => {
        if (!apiRef.current) {
            return;
        }
        const rowsToAdd = [...rows];
        rows.forEach((row) => {
            if (row.clipNb && row.clipNb > 0) {
                rowsToAdd.push({
                    id: `placeholder-children-${row.id}`,
                    hierarchy: [...row.hierarchy, ""],
                });
            }
        });
        apiRef.current.updateRows(rowsToAdd);
    }, []);

    const DataTableMemo = useMemo(
        () => (
            <DataTable
                noControls
                apiRef={apiRef}
                getRowHeight={() => "auto"}
                pageSize={500}
                selectedRows={getSelectedRows}
                rows={rows
                    .map((x, i) => ({ ...x, assetId: x.id }))
                    .filter(
                        (x) =>
                            !filterText ||
                            filterText === "" ||
                            x.name?.toLowerCase().includesAllWords(filterText) ||
                            x.assetId?.toLowerCase().includesAllWords(filterText)
                    )}
                columns={cols}
                getTreeDataPath={getTreeDataPath}
                groupingColDef={CUSTOM_GROUPING_COL_DEF}
                treeData
                columnHeaderHeight={32}
            />
        ),
        [rows, getSelectedRows, cols, fetchingVideoFileData, filterText]
    );

    const NoAssetsMemo = useMemo(
        () => (
            <Stack
                sx={{ position: "relative", width: "100%" }}
                justifyContent="center"
                alignItems="center"
                textAlign="center"
                height={"100%"}
            >
                <Typography variant="h5" component="h2">
                    {t("table:timetoolMediaLibraryTable.body.noAssetMemo")}
                </Typography>
                <Typography mt={1} component="p" color="text.secondary">
                    {t("table:timetoolMediaLibraryTable.body.noAssetMemoSuggest")}
                </Typography>
            </Stack>
        ),
        []
    );

    useEffect(() => {
        if (!apiRef.current || rows.length === 0) return;
        const handleRowExpansionChange: GridEventListener<"rowExpansionChange"> = async (node) => {
            const row = apiRef.current.getRow(node.id) as Row | null;

            if (!node.childrenExpanded || !row || row.childrenFetched) {
                return;
            }

            const childrenRows = await assetService.GetObjectById(row.id);
            updateRows(apiRef, [
                ...(childrenRows.graph?.childObjects?.map((x) => {
                    const clipMetaId = cacheService.GetMetadata().find((x) => x.tagName === "clipInfo")?.id;
                    const frameIn = x.metadata?.find((m) => m.id === clipMetaId)?.value.frameIn;
                    const frameOut = x.metadata?.find((m) => m.id === clipMetaId)?.value.frameOut;

                    return {
                        name: x.name,
                        type: x.type,
                        classification: x.classification,
                        id: `${x.id}`,
                        duration: (frameOut - frameIn) / row.fps,
                        videoRefDuration: row.duration,
                        assetId: row.assetId,
                        src: row.src,
                        fps: row.fps,
                        titleId: row.titleId,
                        clipNb: 0,
                        hierarchy: [row.id, `${x.id}`],
                        childrenFetched: false,
                        frameIn: frameIn,
                        frameOut: frameOut,
                    };
                }) || []),
                { ...row, childrenFetched: true },
                { id: `placeholder-clip-${node.id}`, _action: "delete" },
            ]);
        };

        return apiRef.current?.subscribeEvent("rowExpansionChange", handleRowExpansionChange);
    }, [apiRef]);

    if (!(rows.length > 0)) return NoAssetsMemo;

    return DataTableMemo;
};

export default TimetoolMediaLibraryTable;

interface GroupingCellWithLazyLoadingProps extends GridRenderCellParams<any, any, any, GridGroupNode> {
    hideclipNb?: boolean;
}

const LoadingContainer = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
});

const getTreeDataPath: DataGridProProps["getTreeDataPath"] = (row) => row.hierarchy;

const useUtilityClasses = (ownerState: { classes: DataGridProProps["classes"] }) => {
    const { classes } = ownerState;

    const slots = {
        root: ["treeDataGroupingCell"],
        toggle: ["treeDataGroupingCellToggle"],
    };

    return composeClasses(slots, getDataGridUtilityClass, classes);
};

function GroupingCellWithLazyLoading(props: GroupingCellWithLazyLoadingProps) {
    const { t } = useTranslation();
    const { id, rowNode, row, hideclipNb, formattedValue } = props;

    const rootProps = useGridRootProps();
    const apiRef = useGridApiContext();
    const classes = useUtilityClasses({ classes: rootProps.classes });

    const isLoading = rowNode.childrenExpanded ? !row.childrenFetched : false;

    const Icon = rowNode.childrenExpanded ? rootProps.slots.treeDataCollapseIcon : rootProps.slots.treeDataExpandIcon;

    const handleClick: IconButtonProps["onClick"] = () => {
        apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    };

    return (
        <Box className={classes.root} sx={{ ml: rowNode.depth * 2 }}>
            <div className={classes.toggle}>
                {row.clipNb > 0 &&
                    (isLoading ? (
                        <LoadingContainer>
                            <CircularProgress size="1rem" color="inherit" />
                        </LoadingContainer>
                    ) : (
                        <IconButton
                            size="small"
                            onClick={handleClick}
                            tabIndex={-1}
                            aria-label={
                                rowNode.childrenExpanded
                                    ? apiRef.current.getLocaleText("treeDataCollapse")
                                    : apiRef.current.getLocaleText("treeDataExpand")
                            }
                        >
                            <Icon fontSize="inherit" />
                        </IconButton>
                    ))}
            </div>
            <Stack spacing={0.5} direction={"column"}>
                <Link
                    underline="hover"
                    href={formattedValue === undefined ? routePaths.asset.path + "/" + rowNode.groupingKey : "#"}
                    target="_blank"
                >
                    {formattedValue === undefined ? rowNode.groupingKey : formattedValue}
                </Link>
                {!hideclipNb && row.clipNb > 0 && (
                    <Chip sx={{ position: "relative" }} label={t("common:clipsWithCount", { count: row.clipNb })} />
                )}
            </Stack>
        </Box>
    );
}

const CUSTOM_GROUPING_COL_DEF: GridGroupingColDefOverride = {
    headerName: "Asset Id",
    flex: CellSize.xsmall,
    minWidth: CellSize.xsmall,
    renderCell: (params) => <GroupingCellWithLazyLoading {...(params as GroupingCellWithLazyLoadingProps)} />,
};
